/**
 * AddFeedModal.tsx
 */

import React, { useState, useRef, useEffect } from "react";
import { Modal, TextInput, Button, Group, Text, Select } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconPhoto, IconUpload, IconVideo } from "@tabler/icons-react";
import classes from "./add.module.css";
import { RowDataMain } from "./types";
import useAxiosPrivate from "../../auth/useAxiosPrivate";

interface AddFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddFeed: (newFeedData: RowDataMain) => void;
}

const AddFeedModal: React.FC<AddFeedModalProps> = ({
  isOpen,
  onClose,
  onAddFeed,
}) => {
  const [newFeed, setNewFeed] = useState<RowDataMain>({
    id: "",
    title: "",
    thumbnail: "",
    video: "",
    category: 0,
    show: false,
    action: "Edit",
  });
  const videoDropzoneRef = useRef<() => void>(null);
  const thumbnailDropzoneRef = useRef<() => void>(null);
  const axiosPrivate = useAxiosPrivate();
  const [cat_1, setCat_1] = useState("");
  const [cat_2, setCat_2] = useState("");
  const [cat_3, setCat_3] = useState("");

  const handleVideoUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      // const url = URL.createObjectURL(file);
      setNewFeed({ ...newFeed, video: file });
    }
  };

  const handleThumbnailUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      // const url = URL.createObjectURL(file);
      setNewFeed({ ...newFeed, thumbnail: file });
    }
  };

  const fetchCategory = async () => {
      try {
        const response = await axiosPrivate.get("/api/feed/read-category"); 
        const resDataArr = response.data.data;
        setCat_1(resDataArr[0].name)
        setCat_2(resDataArr[1].name)
        setCat_3(resDataArr[2].name)
      } catch (err) {
        console.error("Failed to fetch contact [ERROR]:", err);
      } finally {
      }
    };

  const handleAddFeed = () => {
    // Check if any required fields are empty
    if (
      !newFeed.title ||
      !newFeed.category ||
      !newFeed.video ||
      !newFeed.thumbnail
      // !newFeed.show ||
    ) {
      alert("Please fill in all the required fields");
      return;
    }

    onAddFeed(newFeed);

    // Clear inputs and reset state
    setNewFeed({
      id: "",
      title: "",
      thumbnail: "",
      video: "",
      category: 0,
      show: false,
      action: "Edit",
    });

    onClose();
  };

  const handleClose = () => {
    // Clear inputs and reset state
    setNewFeed({
      id: "",
      title: "",
      thumbnail: "",
      video: "",
      category: 0,
      show: false,
      action: "Edit",
    });

    // Call the onClose function
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategory();
    }
  }, [isOpen]);

  return (
    <Modal
      style={{ zIndex: "100" }}
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>New Feed</div>
      }
      opened={isOpen}
      onClose={handleClose}
    >
      <TextInput
        label="Title "
        placeholder="Input title"
        required
        mb="sm"
        maxLength={255}
        value={newFeed.title}
        onChange={(event) =>
          setNewFeed({ ...newFeed, title: event.currentTarget.value })
        }
      />

      <Select
          label="Category"
          placeholder="Select Category"
          required
          data={[
              { value: "1", label: cat_1 },
              { value: "2", label: cat_2 },
              { value: "3", label: cat_3 },
          ]}
          onChange={(value) =>{
            setNewFeed({...newFeed, category: value? Number(value) : 0});
          }
        }
      />

      <Text className={classes.Text} mt="sm">
        Video <span style={{ color: 'red' }}>*</span> <span className={classes.format}>(.mp4)</span>
      </Text>
      <Dropzone
        onDrop={handleVideoUpload}
        accept={["video/*"]}
        openRef={videoDropzoneRef}
      >
        <div className={classes.videoContainer}>
          {newFeed.video ? (
            <video controls className={classes.videoSize}>
              <source
                src={URL.createObjectURL(newFeed.video)}
                type="video/mp4"
              />
              The content can't support
            </video>
          ) : (
            <p>
              <IconVideo />
            </p>
          )}
        </div>
      </Dropzone>

      <Group>
        <Button
          onClick={() => videoDropzoneRef.current?.()}
          className={classes.videoButton}
        >
          <IconUpload />
          Upload Video
        </Button>
      </Group>

      <Text className={classes.Text} mt="sm">
        Thumbnail <span style={{ color: 'red' }}>*</span> <span className={classes.format}>(.jpg, .png)</span> 
      </Text>
      <Dropzone
        onDrop={handleThumbnailUpload}
        accept={["image/*"]}
        openRef={thumbnailDropzoneRef}
      >
        <div className={classes.thumbnailContainer}>
          {newFeed.thumbnail ? (
            <img
              src={URL.createObjectURL(newFeed.thumbnail)}
              alt="Thumbnail"
              className={classes.thumbnailSize}
            />
          ) : (
            <p>
              <IconPhoto />
            </p>
          )}
        </div>
      </Dropzone>
      <Group>
        <Button
          onClick={() => thumbnailDropzoneRef.current?.()}
          className={classes.thumbnailButton}
        >
          <IconPhoto />
          Upload Thumbnail
        </Button>
      </Group>

      <Group className={classes.buttonBottom}>
        <Button onClick={() => onClose()} className={classes.buttonBack}>
          Back
        </Button>
        <Button onClick={handleAddFeed} className={classes.buttonSubmit}>
          Submit
        </Button>
      </Group>
    </Modal>
  );
};

export default AddFeedModal;
